import {
  EnvironmentInjector,
  Injectable,
  NgZone,
  Renderer2,
  RendererFactory2,
  createComponent,
  ComponentRef,
  inject
} from '@angular/core';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RaiFaceComponent } from '@svg-icons/rai-face/rai-face.component';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class AnnotationToolService {
  private renderer: Renderer2;
  private isActive = false;
  private highlighter: HTMLElement | null = null;
  private popup: HTMLElement | null = null;
  private rafId: any;
  private selectedElements = new Map<HTMLElement, HTMLElement>();
  private injector = inject(EnvironmentInjector);
  private iconComponentRef: ComponentRef<RaiFaceComponent> | null = null;
  private lastClickX = 0;
  private lastClickY = 0;
  private currentMouseX = 0;
  private currentMouseY = 0;
  private selectionOverlay: HTMLElement | null = null;
  private isMultiSelecting = false;
  private startX = 0;
  private startY = 0;
  private rendererFactory = inject(RendererFactory2);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private utilService = inject(UtilService);
  private route = inject(ActivatedRoute);

  public annotationSent$ = new Subject<{ comment: string; targetElements: Map<HTMLElement, HTMLElement> }>();
  public selectedSids$ = new BehaviorSubject<any[]>([]);

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.init();

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.deactivate();
      this.clearSelections();
      this.closePopup();
    });
  }

  private isInsideApplication(): boolean {
    const url = this.router.url;
    // Typical application routes in this project start with /home/:orgId/:appId
    return url.includes('/home/');
  }

  private isAnnotationEnabled(): boolean {
    return this.utilService.getCookie('enableXP_annotation') === 'true';
  }

  private init() {
    this.createHighlighter();
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('keydown', (e) => this.onKeyDown(e));
      window.addEventListener('keyup', (e) => this.onKeyUp(e));
      window.addEventListener('mousemove', (e) => this.onMouseMove(e));
      window.addEventListener('mousedown', (e) => this.onMouseDown(e), true);
      window.addEventListener('mouseup', (e) => this.onMouseUp(e), true);
      window.addEventListener('click', (e) => this.onClick(e), true);
    });
  }

  private onMouseDown(e: MouseEvent) {
    if (!this.isActive || !e.shiftKey) return;
    if (!this.isInsideApplication() || !this.isAnnotationEnabled()) return;

    // If we already have selected elements from individual clicks,
    // don't allow starting a new free-form drag selection.
    if (this.selectedElements.size > 0 && !this.isMultiSelecting) {
      return;
    }

    this.isMultiSelecting = true;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.createSelectionRect(e.clientX, e.clientY);
  }

  private onMouseUp(e: MouseEvent) {
    if (this.isMultiSelecting) {
      this.isMultiSelecting = false;
      if (this.selectionOverlay) {
        this.renderer.removeChild(document.body, this.selectionOverlay);
        this.selectionOverlay = null;
      }
      if (this.selectedElements.size > 0 && !this.popup) {
        this.showPopup(e.clientX, e.clientY);
      }
    }
  }

  private createSelectionRect(x: number, y: number) {
    this.selectionOverlay = this.renderer.createElement('div');
    this.renderer.setStyle(this.selectionOverlay, 'position', 'fixed');
    this.renderer.setStyle(this.selectionOverlay, 'border', '1px solid #1a73e8');
    this.renderer.setStyle(this.selectionOverlay, 'background', 'rgba(26, 115, 232, 0.1)');
    this.renderer.setStyle(this.selectionOverlay, 'pointer-events', 'none');
    this.renderer.setStyle(this.selectionOverlay, 'z-index', '999999');
    this.renderer.setStyle(this.selectionOverlay, 'left', `${x}px`);
    this.renderer.setStyle(this.selectionOverlay, 'top', `${y}px`);
    this.renderer.appendChild(document.body, this.selectionOverlay);
  }

  private createHighlighter() {
    this.highlighter = this.renderer.createElement('div');
    this.renderer.setStyle(this.highlighter, 'position', 'fixed');
    this.renderer.setStyle(this.highlighter, 'border', '2px dashed #1a73e8');
    this.renderer.setStyle(this.highlighter, 'background', 'rgba(26, 115, 232, 0.1)');
    this.renderer.setStyle(this.highlighter, 'pointer-events', 'none');
    this.renderer.setStyle(this.highlighter, 'z-index', '999999');
    this.renderer.setStyle(this.highlighter, 'display', 'none');
    this.renderer.setAttribute(this.highlighter, 'class', 'annotation-highlighter');
    this.renderer.appendChild(document.body, this.highlighter);
  }

  private onKeyDown(e: KeyboardEvent) {
    if (!this.isInsideApplication() || !this.isAnnotationEnabled()) return;

    if (e.key === 'Meta' || e.key === 'Control') {
      this.clearSelections();
      this.closePopup();
      this.activate();
    }
    if (e.key === 'Escape') {
      this.clearSelections();
      this.closePopup();
    }
  }

  private onKeyUp(e: KeyboardEvent) {
    if (!this.isInsideApplication() || !this.isAnnotationEnabled()) return;

    if (e.key === 'Meta' || e.key === 'Control') {
      this.deactivate();
    }

    if (e.key === 'Shift' || e.key === 'Meta' || e.key === 'Control') {
      if (this.selectedElements.size > 0 && !this.popup) {
        this.showPopup(this.currentMouseX, this.currentMouseY);
      }
    }
  }

  private activate() {
    if (this.isActive || !this.isAnnotationEnabled() || !this.isInsideApplication()) return;
    this.isActive = true;
    document.body.classList.add('annotation-mode');
  }

  private deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    document.body.classList.remove('annotation-mode');
    this.hideHighlighter();
  }

  private onMouseMove(e: MouseEvent) {
    if (!this.isActive) return;

    // Track mouse position always when active, for popup positioning
    this.currentMouseX = e.clientX;
    this.currentMouseY = e.clientY;

    if (this.isMultiSelecting && this.selectionOverlay) {
      const currentX = e.clientX;
      const currentY = e.clientY;
      const x = Math.min(this.startX, currentX);
      const y = Math.min(this.startY, currentY);
      const width = Math.abs(this.startX - currentX);
      const height = Math.abs(this.startY - currentY);

      this.renderer.setStyle(this.selectionOverlay, 'left', `${x}px`);
      this.renderer.setStyle(this.selectionOverlay, 'top', `${y}px`);
      this.renderer.setStyle(this.selectionOverlay, 'width', `${width}px`);
      this.renderer.setStyle(this.selectionOverlay, 'height', `${height}px`);

      this.detectContainedElements(x, y, width, height);
      return;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      // We need to ignore our own highlighters and overlays
      const x = e.clientX;
      const y = e.clientY;

      // Hide highlighter temporarily to detect element below if it's blocking
      if (this.highlighter) this.renderer.setStyle(this.highlighter, 'display', 'none');

      let target = document.elementFromPoint(x, y) as HTMLElement;

      // Restore highlighter
      if (this.highlighter) this.renderer.setStyle(this.highlighter, 'display', 'block');

      // Traverse up if we hit one of our overlays
      while (
        target &&
        (target.classList.contains('annotation-highlighter') || target.classList.contains('annotation-selection'))
      ) {
        // Temporarily hide the overlay to find what is underneath
        const originalDisplay = target.style.display;
        target.style.display = 'none';
        target = document.elementFromPoint(x, y) as HTMLElement;
        target.style.display = originalDisplay;
      }

      if (
        target &&
        target !== this.highlighter &&
        !target.classList.contains('annotation-highlighter') &&
        !target.classList.contains('annotation-selection')
      ) {
        this.highlightElement(target);
      }
    });
  }

  private detectContainedElements(rectX: number, rectY: number, rectWidth: number, rectHeight: number) {
    const selector = '[data-metadata], [data-sid]';
    const elements = document.querySelectorAll(selector);
    const rectRight = rectX + rectWidth;
    const rectBottom = rectY + rectHeight;

    const potentialTargets: HTMLElement[] = [];

    elements.forEach((node) => {
      const el = node as HTMLElement;
      const rect = el.getBoundingClientRect();
      const elMidX = rect.left + rect.width / 2;
      const elMidY = rect.top + rect.height / 2;

      const isInside = elMidX >= rectX && elMidX <= rectRight && elMidY >= rectY && elMidY <= rectBottom;

      if (isInside) {
        potentialTargets.push(el);
      }
    });

    // To prevent selecting the whole container (like a table body) when we only want specific rows,
    // we collect all unique rows or specific leaf elements that are targets.
    const finalSelectionTargets = new Set<HTMLElement>();

    potentialTargets.forEach((el) => {
      const hasBetterChildTarget = potentialTargets.some((other) => other !== el && el.contains(other));

      if (!hasBetterChildTarget) {
        // If the selected leaf element is deep inside a table row,
        // use its closest <tr> as the final target for selection/highlighting.
        const tableRow = el.closest('tr');
        const finalTarget = tableRow && tableRow instanceof HTMLElement ? tableRow : el;
        finalSelectionTargets.add(finalTarget);
      }
    });

    // Cap selection at <tr> level: If a container (non-<tr>) is selected and contains <tr> elements
    // within the selection rectangle, select those rows instead of the container.
    const expandedTargets = new Set<HTMLElement>();

    finalSelectionTargets.forEach((target) => {
      // If the target is already a <tr>, keep it
      if (target.tagName === 'TR') {
        expandedTargets.add(target);
        return;
      }

      // Check if this target contains any <tr> elements within the selection rectangle
      const childRows = target.querySelectorAll('tr');
      let foundChildRows = false;

      childRows.forEach((row) => {
        const rowEl = row as HTMLElement;
        const rowRect = rowEl.getBoundingClientRect();
        const rowMidY = rowRect.top + rowRect.height / 2;

        // Check if this row's midpoint is within the selection rectangle vertically
        if (rowMidY >= rectY && rowMidY <= rectBottom) {
          expandedTargets.add(rowEl);
          foundChildRows = true;
        }
      });

      // If no child rows were found within selection rectangle, keep the original target
      if (!foundChildRows) {
        expandedTargets.add(target);
      }
    });

    expandedTargets.forEach((target) => {
      if (!this.selectedElements.has(target)) {
        this.addSelection(target);
      }
    });
  }

  private onClick(e: MouseEvent) {
    if (!this.isActive) return;

    e.preventDefault();
    e.stopPropagation();

    // Find the actual target (similar logic to mouse move needed to pierce overlays?)
    // Since click events bubble, e.target is usually accurate unless an overlay intercepted it.
    // Our overlays have pointer-events: none, so clicks should pass through to underlying elements.
    const target = e.target as HTMLElement;

    if (target.classList.contains('annotation-highlighter') || target.classList.contains('annotation-selection')) {
      return; // Should not happen with pointer-events: none
    }

    this.lastClickX = e.clientX;
    this.lastClickY = e.clientY;

    if (e.shiftKey) {
      // Prevent individual item selection if a multi-freeForm selection is already in progress
      if (this.isMultiSelecting) return;

      this.toggleSelection(target);
      // Wait for Shift/Meta release to show popup
    } else {
      this.clearSelections();
      this.addSelection(target);
      this.showPopup(e.clientX, e.clientY);
    }

    console.log('Selected elements:', Array.from(this.selectedElements.keys()));
  }

  private showPopup(x: number, y: number) {
    this.closePopup();

    this.popup = this.renderer.createElement('div');
    this.renderer.addClass(this.popup, 'annotation-popup');

    // Container Styles - Wrapper for Icon + Input Box
    this.renderer.setStyle(this.popup, 'position', 'fixed');
    this.renderer.setStyle(this.popup, 'top', `${y + 10}px`);
    this.renderer.setStyle(this.popup, 'left', `${x + 10}px`);
    this.renderer.setStyle(this.popup, 'display', 'flex');
    this.renderer.setStyle(this.popup, 'align-items', 'center');
    this.renderer.setStyle(this.popup, 'z-index', '1000000');

    // Rai Face Icon (Outside the input box)
    const iconWrapper = this.renderer.createElement('div');
    this.renderer.setStyle(iconWrapper, 'display', 'flex');
    this.renderer.setStyle(iconWrapper, 'align-items', 'center');
    this.renderer.setStyle(iconWrapper, 'position', 'relative');
    this.renderer.setStyle(iconWrapper, 'top', '3px');

    // Create the RaiFaceComponent dynamically
    this.iconComponentRef = createComponent(RaiFaceComponent, { environmentInjector: this.injector });
    this.iconComponentRef.instance.width = 30;
    this.iconComponentRef.instance.height = 30;

    // Manually trigger change detection for the new component
    this.iconComponentRef.changeDetectorRef.detectChanges();

    // Append the component's host element to the wrapper
    this.renderer.appendChild(iconWrapper, this.iconComponentRef.location.nativeElement);
    this.renderer.appendChild(this.popup, iconWrapper);

    // Input Container (The "Pill")
    const inputContainer = this.renderer.createElement('div');
    this.renderer.setStyle(inputContainer, 'background', 'white');
    this.renderer.setStyle(inputContainer, 'border', '1px solid #e0e0e0');
    this.renderer.setStyle(inputContainer, 'border-radius', '24px');
    this.renderer.setStyle(inputContainer, 'padding', '4px 4px 4px 16px');
    this.renderer.setStyle(inputContainer, 'display', 'flex');
    this.renderer.setStyle(inputContainer, 'align-items', 'center');
    this.renderer.setStyle(inputContainer, 'box-shadow', '0 4px 10px rgba(0,0,0,0.15)');
    this.renderer.setStyle(inputContainer, 'min-width', '250px');
    this.renderer.setStyle(inputContainer, 'font-family', 'var(--font-family, sans-serif)');

    // Input Field
    const input = this.renderer.createElement('input');
    this.renderer.setAttribute(input, 'type', 'text');
    this.renderer.setStyle(input, 'border', 'none');
    this.renderer.setStyle(input, 'outline', 'none');
    this.renderer.setStyle(input, 'flex-grow', '1');
    this.renderer.setStyle(input, 'font-size', '14px');
    this.renderer.setStyle(input, 'margin-right', '8px');
    this.renderer.setStyle(input, 'color', '#333');

    // Stop propagation on input click to prevent closing popup or changing selection
    this.renderer.listen(input, 'click', (e) => e.stopPropagation());
    this.renderer.listen(input, 'keydown', (e: KeyboardEvent) => {
      e.stopPropagation(); // Allow typing
      if (e.key === 'Escape') {
        this.clearSelections();
        this.closePopup();
      } else if (e.key === 'Enter' && input.value.trim().length > 0) {
        e.preventDefault();
        sendBtn.click();
      }
    });

    // Send Button
    const sendBtn = this.renderer.createElement('button');
    this.renderer.setStyle(sendBtn, 'width', '32px');
    this.renderer.setStyle(sendBtn, 'height', '32px');
    this.renderer.setStyle(sendBtn, 'border-radius', '50%');
    this.renderer.setStyle(sendBtn, 'border', 'none');
    this.renderer.setStyle(sendBtn, 'display', 'flex');
    this.renderer.setStyle(sendBtn, 'justify-content', 'center');
    this.renderer.setStyle(sendBtn, 'align-items', 'center');
    this.renderer.setStyle(sendBtn, 'cursor', 'pointer');
    this.renderer.setStyle(sendBtn, 'transition', 'background 0.2s, color 0.2s');

    // SVG Icon for Arrow (Send) from AppComposerComponent
    sendBtn.innerHTML = `<svg fill="none" preserveAspectRatio="none" viewBox="0 0 20 20" style="width: 20px; height: 20px"><path d="M10 16.6667V3.33333M10 3.33333L5 8.33333M10 3.33333L15 8.33333" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.22222"></path></svg>`;

    // Initial Disabled State matches the design (greyed out)
    const setButtonState = (enabled: boolean) => {
      if (enabled) {
        this.renderer.removeAttribute(sendBtn, 'disabled');
        this.renderer.setStyle(sendBtn, 'background', '#1a73e8');
        this.renderer.setStyle(sendBtn, 'color', 'white');
        this.renderer.setStyle(sendBtn, 'pointer-events', 'auto');
      } else {
        this.renderer.setAttribute(sendBtn, 'disabled', 'true');
        this.renderer.setStyle(sendBtn, 'background', '#f1f3f4');
        this.renderer.setStyle(sendBtn, 'color', '#bdc1c6');
        this.renderer.setStyle(sendBtn, 'pointer-events', 'none');
      }
    };

    setButtonState(false); // Initially disabled

    // Listeners
    this.renderer.listen(input, 'input', (e) => {
      const text = (e.target as HTMLInputElement).value;
      setButtonState(text.trim().length > 0);
    });

    this.renderer.listen(sendBtn, 'click', (e) => {
      e.stopPropagation();
      const comment = input.value;
      console.log('Annotation sent:', {
        comment: comment,
        targetElements: Array.from(this.selectedElements.keys())
      });
      // Emit event
      this.annotationSent$.next({ comment, targetElements: new Map(this.selectedElements) });

      this.closePopup();
      this.clearSelections();
    });

    this.renderer.appendChild(inputContainer, input);
    this.renderer.appendChild(inputContainer, sendBtn);
    this.renderer.appendChild(this.popup, inputContainer);
    this.renderer.appendChild(document.body, this.popup);

    // Focus input automatically
    setTimeout(() => input.focus(), 0);
  }

  private closePopup() {
    if (this.popup) {
      this.renderer.removeChild(document.body, this.popup);
      this.popup = null;
    }
    if (this.iconComponentRef) {
      this.iconComponentRef.destroy();
      this.iconComponentRef = null;
    }
  }

  private toggleSelection(el: HTMLElement) {
    if (this.selectedElements.has(el)) {
      this.removeSelection(el);
    } else {
      this.addSelection(el);
    }
  }

  private addSelection(el: HTMLElement) {
    if (this.selectedElements.has(el)) return;

    // Check for duplicate overlays by checking if an overlay already exists for this element's position/size
    // or if the element is already marked.
    const existingOverlay = this.selectedElements.get(el);
    if (existingOverlay) return;

    const overlay = this.createSelectionOverlay(el);
    this.selectedElements.set(el, overlay);
    this.updateSelectedSids();
  }

  private removeSelection(el: HTMLElement) {
    const overlay = this.selectedElements.get(el);
    if (overlay) {
      this.renderer.removeChild(document.body, overlay);
      this.selectedElements.delete(el);
      this.updateSelectedSids();
    }
  }

  private clearSelections() {
    this.selectedElements.forEach((overlay) => {
      this.renderer.removeChild(document.body, overlay);
    });
    this.selectedElements.clear();
    this.updateSelectedSids();
  }

  private updateSelectedSids() {
    const sids: any[] = [];
    const seen = new Set<string>();
    this.selectedElements.forEach((_, el) => {
      const metadataStr =
        el.getAttribute('data-metadata') || el.closest('[data-metadata]')?.getAttribute('data-metadata');
      if (metadataStr) {
        try {
          const metadata = JSON.parse(metadataStr);
          const key = metadata.sid || metadata.id || JSON.stringify(metadata);
          if (!seen.has(key)) {
            sids.push(metadata);
            seen.add(key);
          }
        } catch (e) {
          console.error('Error parsing metadata', e);
        }
      } else {
        const sid = el.getAttribute('data-sid') || el.closest('[data-sid]')?.getAttribute('data-sid');
        if (sid && sid !== '[object Object]' && !seen.has(sid)) {
          sids.push(sid);
          seen.add(sid);
        }
      }
    });
    this.selectedSids$.next(sids);
  }

  private createSelectionOverlay(el: HTMLElement): HTMLElement {
    const rect = el.getBoundingClientRect();
    const overlay = this.renderer.createElement('div');
    this.renderer.addClass(overlay, 'annotation-selection');
    this.renderer.setStyle(overlay, 'position', 'fixed');
    this.renderer.setStyle(overlay, 'border', '2px solid #1a73e8'); // Solid border for selection
    this.renderer.setStyle(overlay, 'background', 'rgba(26, 115, 232, 0.1)'); // Reduced from 0.2 to 0.1 to prevent dark overlap
    this.renderer.setStyle(overlay, 'pointer-events', 'none');
    this.renderer.setStyle(overlay, 'z-index', '999998'); // Below floating highlighter
    this.renderer.setStyle(overlay, 'top', `${rect.top}px`);
    this.renderer.setStyle(overlay, 'left', `${rect.left}px`);
    this.renderer.setStyle(overlay, 'width', `${rect.width}px`);
    this.renderer.setStyle(overlay, 'height', `${rect.height}px`);
    this.renderer.appendChild(document.body, overlay);
    return overlay;
  }

  private highlightElement(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    if (this.highlighter) {
      this.renderer.setStyle(this.highlighter, 'display', 'block');
      this.renderer.setStyle(this.highlighter, 'top', `${rect.top}px`);
      this.renderer.setStyle(this.highlighter, 'left', `${rect.left}px`);
      this.renderer.setStyle(this.highlighter, 'width', `${rect.width}px`);
      this.renderer.setStyle(this.highlighter, 'height', `${rect.height}px`);
    }
  }

  private hideHighlighter() {
    if (this.highlighter) {
      this.renderer.setStyle(this.highlighter, 'display', 'none');
    }
  }
}
